// src/components/Katie/utility/pageLag.js
// Full-page "lag/glitch" effect (DOM-based). No deps.

export function runPageLag({
    durationMs = 5800,
    beatMs = 180,          // how often to "stutter"
    blurPx = 1.5,         // blur intensity on the underlying page
    skewDeg = 1.2,        // small skew per stutter
    translatePx = 4,      // small x jitter per stutter
    scanLines = 5,        // glitch bar count per stutter
    overlayAlpha = 0.14,  // dark overlay strength
} = {}) {
    // 1) Overlay that covers the whole viewport
    const wrap = document.createElement("div");
    wrap.style.cssText = `
    position: fixed; inset: 0; z-index: 2147483647;
    pointer-events: none; overflow: hidden;
    background: rgba(0,0,0,${overlayAlpha});
  `;
    document.body.appendChild(wrap);

    // 2) Lightly blur & desaturate the underlying page
    const prevFilter = document.documentElement.style.filter;
    document.documentElement.style.filter = `blur(${blurPx}px) saturate(0.95)`;

    // 3) Keyframes (injected once)
    const style = document.createElement("style");
    style.textContent = `
    @keyframes pageLagShake {
      0%   { transform: translate3d(0,0,0) skewX(0deg); }
      50%  { transform: translate3d(${translatePx}px, 0, 0) skewX(${skewDeg}deg); }
      100% { transform: translate3d(0,0,0) skewX(0deg); }
    }
  `;
    document.head.appendChild(style);

    // 4) One stutter pulse = shake + horizontal glitch bars
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;

    const stutter = () => {
        // short global shake by animating the overlay container
        wrap.style.animation = `pageLagShake 110ms ease`;
        // reset the animation so it can re-run
        wrap.addEventListener("animationend", () => { wrap.style.animation = ""; }, { once: true });

        // draw a few white glitch bars that fade out
        for (let i = 0; i < scanLines; i++) {
            const y = Math.floor(12 + Math.random() * (viewportH - 24));
            const h = Math.floor(6 + Math.random() * 16);
            const w = Math.floor(viewportW * (0.35 + Math.random() * 0.65));
            const x = Math.floor(Math.random() * (viewportW - w));

            const bar = document.createElement("div");
            bar.style.cssText = `
        position: absolute; left: ${x}px; top: ${y}px;
        width: ${w}px; height: ${h}px;
        background: rgba(255,255,255,0.16);
        transform: translateX(${(Math.random() * 2 - 1) * 40}px);
        opacity: 1;
        transition: transform 150ms ease, opacity 150ms ease;
      `;
            wrap.appendChild(bar);
            // fade/slide out
            requestAnimationFrame(() => {
                bar.style.transform = `translateX(${(Math.random() * 2 - 1) * 60}px)`;
                bar.style.opacity = "0";
            });
            setTimeout(() => bar.remove(), 160);
        }
    };

    // 5) Schedule pulses over duration
    const pulses = Math.max(3, Math.floor(durationMs / beatMs));
    for (let n = 0; n < pulses; n++) {
        setTimeout(stutter, n * beatMs);
    }

    // 6) Cleanup and resolve when done
    return new Promise((resolve) => {
        setTimeout(() => {
            document.documentElement.style.filter = prevFilter || "";
            try { style.remove(); } catch { }
            try { wrap.remove(); } catch { }
            resolve();
        }, durationMs + 50);
    });
}

// Optional global access (so you can call from anywhere without import)
if (typeof window !== "undefined") window.runPageLag = runPageLag;
